/**
 * The model of the « Journal des réclamations » (/achats/reclamations — design/ux/03 §5.4, §6.6), of the
 * réclamation document (/achats/reclamations/:id — §5.5-§5.7) and of the §B2 drawer / button of S1 and
 * S3 (§5.2.1; refonte WP-35). Pure functions, unit tested: the pages own no rule. The server stays the
 * authority — it filters, counts, sums, lists the actions open to the pharmacy (`actions`, from the
 * D-20 transition table) and refuses anything else; this module only reads those answers so a screen
 * never offers an action that would be refused, and says why before the request.
 *
 *   · query      `paramsJournalReclamations` — the filter bar + the view → the server's query
 *   · payload    `lireJournalReclamations` — the server envelope (`{ data, total, compteurs, totaux }`)
 *   · views      `ongletsVues`, footer `piedJournalReclamations`, cells `libelleNumero`, `titreLigne`
 *   · rights     `droitsReclamations`, `actionsReclamation`
 *   · document   `phraseStatut`, `banniereProposition`, `lignesDocument`, `filDocument`, `canalEnvoi`
 *   · S1 / S3    `boutonReclamations`, `reclamationsOuvertes`
 *   · print      `descriptionFiltresReclamations`
 */
import {
  ACTION_RECLAMATION as A,
  COTE_RECLAMATION,
  DEDUCTION_STOCK_RECLAMATION,
  ETAT_PROPOSITION_RECLAMATION,
  LIBELLES_DEDUCTION_STOCK_RECLAMATION,
  LIBELLES_MOTIF_RECLAMATION,
  LIBELLES_STATUT_RECLAMATION,
  RECLAMATION_BORNES,
  STATUTS_OUVERTS_RECLAMATION,
  STATUT_RECLAMATION as S,
  TYPE_EVENEMENT_RECLAMATION as T,
} from "@pharmakon/shared/constants/reclamations.js";
import { STATUT_COMPTABLE_AVOIR, TYPE_AVOIR } from "@pharmakon/shared/constants/avoirs.js";
import { CYCLE_AV } from "@/config/avoir-editeur.messages.js";
import { can } from "@/config/permissions.config.js";
import { fmtDate, fmtMoney, fmtTime } from "@/config/locale.config.js";
import { paramsJournal } from "@/lib/achat-journal.js";
import { ORDRE_VUES_RECLAMATION, VUE_DEFAUT_RECLAMATION } from "@/config/reclamation.config.js";
import { LIBELLE_CANAL, M_RAVOIR, M_RB2, M_RD, M_RFIL_EVENEMENTS, M_RJ, M_RTIROIR } from "@/config/reclamation.messages.js";

const da = (v) => fmtMoney(v, { noSuffix: true });
const dateHeure = (d) => (d ? `${fmtDate(d)} ${fmtTime(d)}` : "");

// ─── Query and payload ───────────────────────────────────────────────────────────────────────────

/** The view of the URL, or the default one (an unknown value never reaches the server). */
export const vueValide = (v) => (ORDRE_VUES_RECLAMATION.includes(v) ? v : VUE_DEFAUT_RECLAMATION);

/**
 * The `GET /reclamations` query: the filter bar's values (lists comma-joined, empty values dropped),
 * the view, the sort and the page. The view narrows the rows and the footer — the view counts stay
 * those of the other filters (server rule).
 * @param {Record<string, unknown>} queryParams `useFilters().queryParams`
 * @param {{ vue?: string, sort?: { by: string, order: string } | null, page?: number, limit?: number }} o
 */
export function paramsJournalReclamations(queryParams, { vue, sort = null, page, limit } = {}) {
  const out = paramsJournal(queryParams, { onglet: undefined, sort, page, limit });
  delete out.onglet;
  if (page == null) delete out.page;
  if (limit == null) delete out.limit;
  out.vue = vueValide(vue);
  return out;
}

/** The journal page from the server envelope (the API client resolves with the BODY). */
export function lireJournalReclamations(body) {
  const reclamations = Array.isArray(body?.data) ? body.data : [];
  return {
    reclamations,
    total: Number.isInteger(body?.total) ? body.total : reclamations.length,
    page: Number.isInteger(body?.page) ? body.page : 1,
    limit: Number.isInteger(body?.limit) ? body.limit : RECLAMATION_BORNES.page_taille,
    compteurs: body?.compteurs ?? null,
    totaux: body?.totaux ?? null,
  };
}

// ─── Views, footer, cells ────────────────────────────────────────────────────────────────────────

/** The view tabs (PageHeader `tabs`) with the server counts; none while they are not trustworthy. */
export function ongletsVues(compteurs, fiables) {
  return ORDRE_VUES_RECLAMATION.map((v) => ({
    value: v,
    label: M_RJ.vues[v],
    count: fiables && compteurs ? compteurs[v] ?? 0 : undefined,
  }));
}

/** ux/03 §5.4 — the footer over the whole filter (the server's sums). */
export function piedJournalReclamations(totaux, { compact = false } = {}) {
  if (!totaux) return "";
  const v = { nombre: totaux.nombre ?? 0, montant: da(totaux.montant_reclame ?? 0), ouvertes: da(totaux.montant_ouvertes ?? 0) };
  return compact ? M_RJ.piedCompact(v) : M_RJ.pied(v);
}

/** The N° cell: the REC number, or « Brouillon du … » (a brouillon has no number — ux/03 §0.8). */
export const libelleNumero = (r) => r?.numero ?? M_RJ.brouillonDu(fmtDate(r?.created_at ?? r?.date));

/** The row tooltip at 1024 (facture and activity leave the grid, ux/03 §5.4). */
export const titreLigne = (r) => M_RJ.ligneTitre({ facture: r?.facture_achat?.libelle ?? null, activite: r?.activite ? fmtDate(r.activite) : null });

/** The words of a state (the chip text) and of a motif. */
export const libelleStatut = (s) => LIBELLES_STATUT_RECLAMATION[s] ?? "—";
export const libelleMotif = (m) => LIBELLES_MOTIF_RECLAMATION[m] ?? "—";

/** The status registry key of a state (Badge `status`). */
export const statutBadge = (s) => `reclamation.${s}`;

// ─── Rights and actions ──────────────────────────────────────────────────────────────────────────

/** The réclamation rights of the signed-in user (UI gating; the server enforces — A-07, A-61 (1)). */
export function droitsReclamations(user) {
  return {
    lire: can(user, "reclamation", "read"),
    creer: can(user, "reclamation", "create"),
    modifier: can(user, "reclamation", "update"),
    supprimer: can(user, "reclamation", "delete"),
    envoyer: can(user, "reclamation", "envoyer"),
    repondre: can(user, "reclamation", "repondre"),
    cloturer: can(user, "reclamation", "cloturer"),
    genererAvoir: can(user, "reclamation", "generer_avoir"),
    // the avoir editor's own right (« Ouvrir dans l'éditeur d'avoir »)
    editeurAvoir: can(user, "achat", "update"),
  };
}

/** The right each server action needs. */
const DROIT_ACTION = Object.freeze({
  [A.MODIFIER]: "modifier",
  [A.SUPPRIMER]: "supprimer",
  [A.ENVOYER]: "envoyer",
  [A.RESERVER_NUMERO]: "envoyer",
  [A.MESSAGE]: "repondre",
  [A.CONSIGNER]: "repondre",
  [A.ACCEPTER_PROPOSITION]: "repondre",
  [A.REFUSER_PROPOSITION]: "repondre",
  [A.RELANCER]: "repondre",
  [A.GENERER_AVOIR]: "genererAvoir",
  [A.CLOTURER]: "cloturer",
  [A.RETIRER]: "cloturer",
  [A.PIECE_JOINTE]: "modifier",
  // refonte WP-88 / WP-89 — the stock of the claimed lines after the send (routes: envoyer / cloturer)
  [A.DEDUIRE_STOCK]: "envoyer",
  [A.REINTEGRER_STOCK]: "cloturer",
});

/**
 * The actions a réclamation offers to this user: the server's `actions` (D-20 table, side and channel
 * already applied) AND the user's right. The relance carries its CONFIG delay (OP-03-34) as a reason;
 * the letter can be printed once numbered (or a number can be reserved for it).
 * @param {{ actions?: string[], numero?: string|null, statut: string, relance_possible_le?: string|null, canal?: string|null }} doc
 * @param {ReturnType<typeof droitsReclamations>} droits
 * @param {Date} [maintenant]
 */
export function actionsReclamation(doc, droits, maintenant = new Date()) {
  const ouvertes = new Set(doc?.actions ?? []);
  const peut = (a) => ouvertes.has(a) && Boolean(droits?.[DROIT_ACTION[a]]);
  const raisons = {};
  const relanceLe = doc?.relance_possible_le ? new Date(doc.relance_possible_le) : null;
  const relanceTot = Boolean(relanceLe && relanceLe > maintenant);
  if (relanceTot) raisons.relancer = M_RD.raisons.relanceTot(dateHeure(relanceLe));
  if (ouvertes.has(A.RELANCER) && !droits?.repondre) raisons.relancer = M_RD.raisons.droits;
  const brouillon = doc?.statut === S.BROUILLON;
  return {
    modifier: peut(A.MODIFIER),
    supprimer: peut(A.SUPPRIMER),
    envoyer: peut(A.ENVOYER),
    reserverNumero: peut(A.RESERVER_NUMERO),
    message: peut(A.MESSAGE),
    pieceJointe: peut(A.PIECE_JOINTE),
    consigner: peut(A.CONSIGNER),
    accepterProposition: peut(A.ACCEPTER_PROPOSITION),
    refuserProposition: peut(A.REFUSER_PROPOSITION),
    relancer: peut(A.RELANCER) && !relanceTot,
    relanceVisible: ouvertes.has(A.RELANCER),
    genererAvoir: peut(A.GENERER_AVOIR),
    cloturer: peut(A.CLOTURER),
    retirer: peut(A.RETIRER),
    deduireStock: peut(A.DEDUIRE_STOCK),
    reintegrerStock: peut(A.REINTEGRER_STOCK),
    // the A4 letter (§6.5): a numbered réclamation, or a brouillon whose number can be reserved now
    imprimer: Boolean(droits?.lire) && (Boolean(doc?.numero) || (brouillon && peut(A.RESERVER_NUMERO))),
    raisons,
  };
}

// ─── Channels (A-19, OP-03-33) ───────────────────────────────────────────────────────────────────

/** The way a réclamation leaves (and the composer's primary button): PLATEFORME | EMAIL | IMPRESSION. */
export function canalEnvoi(fournisseur) {
  if (!fournisseur) return null;
  if (fournisseur.est_connecte) return "PLATEFORME";
  return fournisseur.email_contact || fournisseur.email ? "EMAIL" : "IMPRESSION";
}

/** « c***@pde.dz » — an address shown before sending (the server masks the sent one the same way). */
export function masquerEmail(email) {
  const e = typeof email === "string" ? email.trim() : "";
  const at = e.indexOf("@");
  if (at < 1) return e ? "***" : "";
  return `${e[0]}***${e.slice(at)}`;
}

// ─── Document (§5.5) ─────────────────────────────────────────────────────────────────────────────

/** ux/03 §5.1 « Pharmacy sees » — the sentence of the state band. */
export function phraseStatut(doc) {
  if (!doc) return "";
  const f = doc.fournisseur?.nom ?? "";
  switch (doc.statut) {
    case S.BROUILLON:
      return doc.numero ? M_RD.statut.BROUILLON_NUMERO(doc.numero) : M_RD.statut.BROUILLON;
    case S.ENVOYEE:
      return M_RD.statut.ENVOYEE(fmtDate(doc.envoyee_at), LIBELLE_CANAL(doc.canal));
    case S.EN_COURS:
      return doc.issue_par_fournisseur
        ? M_RD.statut.EN_COURS_FOURNISSEUR(f, fmtDate(doc.prise_en_charge_at))
        : M_RD.statut.EN_COURS_CONSIGNEE(fmtDate(doc.prise_en_charge_at));
    case S.RESOLUE:
      return M_RD.statut.RESOLUE(fmtDate(doc.resolue_at), doc.resolution ?? "—");
    case S.REJETEE:
      return M_RD.statut.REJETEE(fmtDate(doc.rejetee_at), doc.motif_rejet ?? "—");
    case S.CLOTUREE:
      return doc.motif_retrait ? M_RD.statut.RETIREE(fmtDate(doc.cloturee_at), doc.motif_retrait) : M_RD.statut.CLOTUREE(fmtDate(doc.cloturee_at));
    default:
      return "";
  }
}

/** The proposition banner (§5.5) while the fournisseur's proposal awaits the pharmacy — else null. */
export function banniereProposition(doc) {
  const p = doc?.proposition;
  if (!p || p.etat !== ETAT_PROPOSITION_RECLAMATION.EN_ATTENTE) return null;
  const lignes = (p.lignes ?? []).filter((l) => Number(l.quantite) > 0).length;
  return { texte: M_RD.proposition({ fournisseur: doc.fournisseur?.nom ?? "", montant: p.montant ? da(p.montant) : null, lignes, message: p.message }) };
}

/** The « avoir généré » sentence and link, null without one. */
export function avoirLie(doc) {
  const a = doc?.avoir;
  if (!a) return null;
  return {
    id: a.id,
    numero: a.numero_avoir,
    texte: a.annule ? M_RD.avoirAnnule(a.numero_avoir) : M_RD.avoirGenere(a.numero_avoir, fmtDate(a.date_avoir)),
    annule: Boolean(a.annule),
  };
}

/** The produit line of a réclamation line (« AMOXICILLINE 1 G »). */
export const nomProduit = (p) => [p?.nom_commercial, p?.dosage].filter(Boolean).join(" ") || "—";

/**
 * Refonte WP-89 (D-36, A-83) — a line's stock, pharmacy plane only: « Déduit du stock : 2 le 02/11/2026 »
 * (« · 2 réintégrées » once given back), « Déduction à la création de l'avoir », or nothing (a motif
 * without deduction, a line sent before WP-88).
 */
export function stockLigneDocument(l) {
  if (!l?.deduction_stock) return null;
  const libelle = l.deduction_stock_libelle ?? LIBELLES_DEDUCTION_STOCK_RECLAMATION[l.deduction_stock] ?? null;
  if (l.deduction_stock !== DEDUCTION_STOCK_RECLAMATION.MAINTENANT) return libelle;
  const base = M_RD.stockLigne.deduit(libelle, l.quantite_deduite ?? l.quantite_reclamee, l.deduit_at ? fmtDate(l.deduit_at) : null);
  return (l.quantite_reintegree ?? 0) > 0 ? `${base} · ${M_RD.stockLigne.reintegree(l.quantite_reintegree)}` : base;
}

/** The lines of the document: produit, lot, Fact. / Reçue, réclamé, accordé (once resolved), montant, stock (WP-89). */
export function lignesDocument(doc) {
  return (doc?.lignes ?? []).map((l) => ({
    id: l.id,
    produit: nomProduit(l.produit),
    lot: l.numero_lot ?? l.stock_lot?.numero_lot ?? "—",
    factRecue: l.quantite_facturee != null ? M_RD.factRecue(l.quantite_facturee, l.quantite_recue) : "—",
    reclamee: l.quantite_reclamee,
    accordee: l.quantite_accordee,
    montant: l.montant,
    motif: l.motif_ligne_libelle ?? null,
    stock: stockLigneDocument(l),
  }));
}

/** The words of one timeline event (§5.5 « Historique et échanges »). */
function texteEvenement(e, doc) {
  const fournisseur = doc?.fournisseur?.nom ?? "le fournisseur";
  const W = M_RFIL_EVENEMENTS;
  switch (e.type) {
    case T.CREATION:
      return W.CREATION({ auteur: e.auteur });
    case T.ENVOI:
      return W.ENVOI({ canal: e.canal, destinataire: e.destinataire_masque, auteur: e.auteur });
    case T.ENVOI_ECHEC:
      return W.ENVOI_ECHEC({ message: e.message });
    case T.RELANCE:
      return W.RELANCE({ canal: e.canal });
    case T.PRISE_EN_CHARGE:
      return W.PRISE_EN_CHARGE({ fournisseur });
    case T.MESSAGE:
      if (e.cote === COTE_RECLAMATION.FOURNISSEUR) return W.MESSAGE_FOURNISSEUR({ fournisseur });
      return e.payload?.interne ? W.NOTE_INTERNE({ auteur: e.auteur }) : W.MESSAGE_PHARMACIE({ auteur: e.auteur });
    case T.PIECE_JOINTE:
      return W.PIECE_JOINTE({ n: (e.payload?.pieces ?? []).length || 1, cote: e.cote, fournisseur });
    case T.PROPOSITION:
      return W.PROPOSITION({ fournisseur, montant: e.payload?.montant ? da(e.payload.montant) : null });
    case T.PROPOSITION_ACCEPTEE:
      return W.PROPOSITION_ACCEPTEE();
    case T.PROPOSITION_REFUSEE:
      return W.PROPOSITION_REFUSEE();
    case T.REPONSE_CONSIGNEE:
      return W.REPONSE_CONSIGNEE({ statut: e.statut_apres ? libelleStatut(e.statut_apres) : null });
    case T.RESOLUTION:
      return W.RESOLUTION({ fournisseur });
    case T.REJET:
      return W.REJET({ fournisseur });
    case T.AVOIR_GENERE:
      return W.AVOIR_GENERE({ numero: e.payload?.numero_avoir, montant: e.payload?.montant_ttc ? da(e.payload.montant_ttc) : null });
    case T.CLOTURE:
      return W.CLOTURE();
    case T.RETRAIT:
      return W.RETRAIT();
    case T.DEDUCTION_STOCK:
      return W.DEDUCTION_STOCK({ lignes: e.payload?.lignes, auteur: e.auteur });
    case T.RESTITUTION_STOCK:
      return W.RESTITUTION_STOCK({ lignes: e.payload?.lignes, auteur: e.auteur });
    default:
      return e.type;
  }
}

/** Pharmacy-only events (never on the other plane): delivery failures, consigned answers, stock moves (WP-88), notes. */
const INTERNES = new Set([T.ENVOI_ECHEC, T.REPONSE_CONSIGNEE, T.DEDUCTION_STOCK, T.RESTITUTION_STOCK]);
const estInterne = (e) => INTERNES.has(e.type) || (e.type === T.MESSAGE && e.cote === COTE_RECLAMATION.PHARMACIE && Boolean(e.payload?.interne));

/**
 * The timeline (oldest first, as the server orders it): when, who / what, the message text, the
 * side (the fournisseur's events stand apart) and the « interne » mark, the attachments it added.
 */
export function filDocument(doc) {
  return (doc?.evenements ?? []).map((e) => ({
    id: e.id,
    quand: dateHeure(e.date),
    texte: texteEvenement(e, doc),
    message: e.message ?? null,
    cote: e.cote,
    fournisseur: e.cote === COTE_RECLAMATION.FOURNISSEUR,
    interne: estInterne(e),
    echec: e.type === T.ENVOI_ECHEC,
    pieces: e.type === T.PIECE_JOINTE ? (e.payload?.pieces ?? []) : [],
    avoirId: e.type === T.AVOIR_GENERE ? (e.payload?.avoir_id ?? null) : null,
  }));
}

/** The attachments of the document, with their size in words and their side. */
export function piecesDocument(doc, tailleLisible) {
  return (doc?.pieces_jointes ?? []).map((p) => ({
    id: p.id,
    nom: p.nom_fichier,
    taille: tailleLisible ? tailleLisible(p.taille_octets) : String(p.taille_octets),
    cote: M_RD.pieceCote[p.cote] ?? "",
    mime: p.mime,
  }));
}

// ─── « Générer l'avoir » (§5.7) ──────────────────────────────────────────────────────────────────

/** The A-02 words of a solde (`{ sens, montant }` of the server): « 1 200,00 DA à payer », « crédit … », « à jour ». */
export function texteSolde(s) {
  if (!s) return "—";
  if (s.sens === "CREDIT") return M_RAVOIR.soldeSens.CREDIT(da(s.montant));
  if (s.sens === "A_PAYER") return M_RAVOIR.soldeSens.A_PAYER(da(s.montant));
  return M_RAVOIR.soldeSens.A_JOUR;
}

/**
 * Refonte WP-83 — the lifecycle « Générer l'avoir » preselects, by A-82 (« created en instance unless the
 * supplier has accepted »): a Résolue réclamation was accepted by the fournisseur → « Confirmé par le
 * fournisseur »; any earlier state (Envoyée, En cours — WP-88) → « Enregistrer en instance ». The user
 * still chooses (D-32): both options state their effect before the avoir is written.
 */
export function statutCycleParDefaut(reclamation) {
  return reclamation?.statut === S.RESOLUE ? STATUT_COMPTABLE_AVOIR.CONFIRME : STATUT_COMPTABLE_AVOIR.INSTANCE;
}

/**
 * Refonte WP-83 (CP §3.2, §11, Scenario J; D-32, A-104 (10), A-115 (4)) — what each lifecycle choice of
 * « Générer l'avoir » does to the fournisseur's solde, from the server's preview (`solde_avant`,
 * `solde_apres_instance`, `solde_apres_confirme`, `statut_comptable_requis`): « en instance » leaves
 * the solde as it is and adds the TTC to the pending credits; « confirmé » lowers it now. Null when the
 * avoir has no choice (a ristourne — revenue, no solde effect).
 */
export function effetsCycleGeneration(apercu) {
  if (!apercu?.statut_comptable_requis) return null;
  const ttc = da(apercu.montant_ttc);
  return {
    [STATUT_COMPTABLE_AVOIR.INSTANCE]: {
      lignes: [CYCLE_AV.soldeInchange(texteSolde(apercu.solde_apres_instance ?? apercu.solde_avant)), CYCLE_AV.attenteAjout(ttc)],
    },
    [STATUT_COMPTABLE_AVOIR.CONFIRME]: {
      lignes: [CYCLE_AV.soldeAvantApres(texteSolde(apercu.solde_avant), texteSolde(apercu.solde_apres_confirme))],
    },
  };
}

/**
 * « Créer l'avoir » (refonte WP-89; brief 2026-11-01 §2.4, K1 A5 (1)) — the editor's one entry from a
 * réclamation: `/avoirs/nouveau?reclamation=<id>` (the editor reads WP-88's prefill: source, lines,
 * connection, issue, proposed lifecycle — every value editable there), plus `type` when the user chose
 * one for a motif without default (Retard, Autre — « Générer l'avoir » asks it first).
 */
export function lienEditeurAvoir(doc, { type = null } = {}) {
  const p = new URLSearchParams();
  if (doc?.id) p.set("reclamation", doc.id);
  if (type && Object.values(TYPE_AVOIR).includes(type)) p.set("type", type);
  const q = p.toString();
  return `/avoirs/nouveau${q ? `?${q}` : ""}`;
}

/**
 * Refonte WP-89 (K1 A5 (1)) — what F9 does on the document: « Accepter la proposition » while it is
 * offered (« Créer l'avoir » then has no key), else « Créer l'avoir » (the editor), else — without the
 * editor's right — WP-83's « Générer l'avoir »; nothing once an avoir is linked.
 * @returns {"ACCEPTER_PROPOSITION"|"CREER_AVOIR"|"GENERER_AVOIR"|null}
 */
export function actionPrincipaleDocument(doc, actions, droits) {
  if (banniereProposition(doc) && actions?.accepterProposition) return "ACCEPTER_PROPOSITION";
  if (doc?.avoir || !actions?.genererAvoir) return null;
  return droits?.editeurAvoir ? "CREER_AVOIR" : "GENERER_AVOIR";
}

// ─── S1 / S3 (§B2, §5.2.1) and the facture's open réclamations ───────────────────────────────────

/**
 * The « Réclamations · n » button of the fournisseur strip (OP-03-35): n = open réclamations (warning
 * tone when > 0), the tooltip « o ouverte(s) · r résolue(s) · c clôturée(s) »; without counts (still
 * loading, failed) the plain label.
 * @param {{ ouvertes: number, resolues: number, cloturees: number } | null} compteurs
 */
export function boutonReclamations(compteurs) {
  if (!compteurs) return { label: M_RB2.bouton, titre: M_RB2.aideIndisponible, ton: "neutral", n: null };
  const n = Number(compteurs.ouvertes ?? 0);
  return {
    label: n > 0 ? M_RB2.boutonCompte(n) : M_RB2.bouton,
    titre: M_RB2.aide({ ouvertes: n, resolues: Number(compteurs.resolues ?? 0), cloturees: Number(compteurs.cloturees ?? 0) }),
    ton: n > 0 ? "warning" : "neutral",
    n,
  };
}

/** S3 « À traiter » — the facture's open réclamations (`GET /achats/:id` `reclamations`), newest first. */
export function reclamationsOuvertes(fiche) {
  return (fiche?.reclamations ?? [])
    .filter((r) => r.ouverte ?? STATUTS_OUVERTS_RECLAMATION.includes(r.statut))
    .map((r) => ({ id: r.id, texte: M_RB2.ouverte(r.numero ?? `${M_RB2.brouillon} ${fmtDate(r.date)}`) }));
}

/** The drawer's header counts « 1 ouverte · 4 clôturées » (§5.2.1). */
export const compteursTiroir = (c) => (c ? M_RTIROIR.compteurs({ ouvertes: Number(c.ouvertes ?? 0), cloturees: Number(c.cloturees ?? 0) }) : "");

// ─── Print (§6.6) ────────────────────────────────────────────────────────────────────────────────

/** The « active filters » line of the printed journal. */
export function descriptionFiltresReclamations({ vue, fournisseurs = [], motifs = [], recherche = "" } = {}) {
  const parts = [];
  if (vue) parts.push(`${M_RJ.vuesLabel} : ${M_RJ.vues[vue] ?? vue}`);
  if (fournisseurs.length) parts.push(`${M_RJ.filtres.fournisseur} : ${fournisseurs.join(", ")}`);
  if (motifs.length) parts.push(`${M_RJ.filtres.motif} : ${motifs.map((m) => libelleMotif(m)).join(", ")}`);
  if (recherche) parts.push(`« ${recherche} »`);
  return parts.join(" · ");
}
